import { useEffect, useRef, useCallback } from 'react';
import { apiClient } from '../api/client';

// Интерфейс уведомления
interface Notification {
  id: number;
  notification_type: string;
  title: string;
  message: string;
  amount?: number;
  created_at: string;
  metadata?: {
    limit?: number;
    percentage?: number;
    level?: string;
  };
}

interface UseNotificationPollerOptions {
  enabled?: boolean;
  interval?: number; // в миллисекундах
  onNewNotification?: (notification: Notification) => void;
}

/**
 * Hook для периодической проверки новых уведомлений через API
 * Используется для показа попапов уведомлений в веб-приложении
 */
export function useNotificationPoller({
  enabled = true,
  interval = 60000, // по умолчанию каждые 60 секунд
  onNewNotification
}: UseNotificationPollerOptions = {}) {
  const lastCheckRef = useRef<Date>(new Date());
  const timeoutRef = useRef<NodeJS.Timeout>();
  const seenNotificationsRef = useRef<Set<number>>(new Set());

  const checkForNewNotifications = useCallback(async () => {
    // Проверяем наличие токена перед запросом
    const token = localStorage.getItem('auth_token');
    if (!token) {
      // Нет токена - пропускаем проверку
      return;
    }
    
    try {
      // Получаем уведомления созданные после последней проверки
      const response = await apiClient.getNotifications();
      
      if (response.notifications && response.notifications.length > 0) {
        const newNotifications = response.notifications.filter((notif: Notification) => {
          const createdAt = new Date(notif.created_at);
          const isNew = createdAt > lastCheckRef.current && !seenNotificationsRef.current.has(notif.id);
          
          // Показываем все новые уведомления
          return isNew;
        });

        // Обрабатываем новые уведомления
        for (const notification of newNotifications) {
          seenNotificationsRef.current.add(notification.id);
          
          if (onNewNotification) {
            onNewNotification(notification);
          }
        }
      }

      lastCheckRef.current = new Date();
    } catch (error) {
      // Тихая обработка ошибок polling - не логируем каждую ошибку
      // Ошибки сети или временные проблемы не должны засорять логи
    }
  }, [onNewNotification]);

  useEffect(() => {
    if (!enabled) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      return;
    }

    const poll = () => {
      checkForNewNotifications();
      timeoutRef.current = setTimeout(poll, interval);
    };

    // Первая проверка сразу
    checkForNewNotifications();
    
    // Запускаем polling
    timeoutRef.current = setTimeout(poll, interval);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [enabled, interval, checkForNewNotifications]);

  return {
    checkNow: checkForNewNotifications
  };
}

